import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { TbChevronDown } from 'react-icons/tb';

import {
  ApolloError,
  Checkbox,
  HStack,
  Icon,
  LoadingIndicator,
  SimpleDrawer,
  TableView,
  Text,
  VStack,
} from '../../core-ui';
import formatCurrency from '../../core/support/formatCurrency';

import { useSeason } from '../SeasonState';

import { Adjustment, InvoiceDetailView } from './InvoiceDetailView';
import type {
  GetInvoiceSendingData,
  GetInvoiceSendingDataVariables,
  GetInvoiceSendingData_season_vendors as Vendor,
} from './__generated__/GetInvoiceSendingData';

const GET_INVOICE_SENDING_DATA = gql`
  query GetInvoiceSendingData(
    $seasonId: String!
    $vendorIds: [String!]!
    $selectedDates: [String!]!
    $includeSalesData: Boolean!
  ) {
    myAccount {
      id
      settings {
        currency
      }
    }
    season(id: $seasonId) {
      id
      vendors(idIn: $vendorIds) {
        id
        name
        contact
        category
        createdAt
        status
        balance
        invoicePreview(
          selectedDates: $selectedDates
          includeSalesData: $includeSalesData
        ) {
          taxPercent
          totalPrice
        }
      }
    }
  }
`;

type Props = {
  selectedDates: Set<string>;
  selectedVendors: Set<string>;
  adjustments: Map<string, Adjustment>;
  signupFeeOnly: boolean;
  includeSalesData: boolean;
  setIncludeSalesData: (includeSalesData: boolean) => void;
};

export function getBalance(vendor: Vendor, adjustment?: Adjustment) {
  const oldBalance = vendor.balance;
  const invoiceTotal = vendor.invoicePreview.totalPrice;
  const taxPercent = vendor.invoicePreview.taxPercent;
  const adjustmentAmount = adjustment?.amount ?? 0;
  const adjustmentTaxAmount = adjustment?.taxable
    ? adjustmentAmount * taxPercent
    : 0;
  const adjustmentAmountWithTax = adjustmentAmount + adjustmentTaxAmount;
  const newBalance = oldBalance + invoiceTotal + adjustmentAmountWithTax;

  return newBalance;
}

export default function ReviewInvoicePanel(props: Props) {
  const {
    selectedDates,
    selectedVendors,
    adjustments,
    signupFeeOnly,
    includeSalesData,
    setIncludeSalesData,
  } = props;
  const [sortBy, setSortBy] = useState<[number, boolean]>([0, true]);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);

  const { season } = useSeason();

  const { data, loading, error } = useQuery<
    GetInvoiceSendingData,
    GetInvoiceSendingDataVariables
  >(GET_INVOICE_SENDING_DATA, {
    variables: {
      seasonId: season?.id ?? '',
      vendorIds: Array.from(selectedVendors),
      selectedDates: Array.from(selectedDates),
      includeSalesData,
    },
    fetchPolicy: 'cache-and-network',
  });

  if (error) {
    return <ApolloError error={error} />;
  }

  if (loading || !data) {
    return <LoadingIndicator />;
  }

  if (!data.season) {
    return <p>{t('Season not found')}</p>;
  }

  const { vendors } = data.season;
  const { settings } = data.myAccount;
  const currency = settings.currency ?? 'USD';

  const selectedVendor = vendors.find(({ id }) => id === selectedVendorId);

  return vendors.length === 0 ? (
    <VStack padding={5} textAlign="center">
      <Text>{t('No invoices to display')}</Text>
    </VStack>
  ) : (
    <>
      {!signupFeeOnly && (
        <HStack p={6}>
          <Checkbox
            isChecked={includeSalesData}
            onChange={(e) => setIncludeSalesData(e.target.checked)}
          />
          <Text>{t('Include sales and redemption data')}</Text>
        </HStack>
      )}
      <TableView
        data={vendors}
        columns={[
          {
            title: t('Name'),
            thProps: { width: '60%' },
            render: (vendor) => vendor.name,
            sortable: { sortFn: (a, b) => a.name.localeCompare(b.name) },
          },
          {
            title: t('Contact'),
            thProps: { width: '40%' },
            render: (vendor) => vendor.contact,
            sortable: {
              sortFn: (a, b) => a.contact.localeCompare(b.contact),
            },
          },
          {
            title: t('Balance'),
            thProps: { width: 1 },
            tdProps: { isNumeric: true },
            thAlign: 'end',
            render: (vendor) => {
              const balance = Math.round(
                getBalance(vendor, adjustments.get(vendor.id)) * 100,
              );
              return (
                <Text
                  textAlign="right"
                  color={balance > 0 ? 'red.500' : 'green.500'}
                >
                  {formatCurrency(balance, currency)}
                </Text>
              );
            },
            sortable: {
              sortFn: (a, b) =>
                getBalance(a, adjustments.get(a.id)) -
                getBalance(b, adjustments.get(b.id)),
            },
          },
          {
            title: '',
            thProps: { width: 1 },
            render: () => {
              return (
                <VStack
                  width="20px"
                  height="20px"
                  justify="center"
                  align="center"
                >
                  <Icon as={TbChevronDown} fontSize={20} />
                </VStack>
              );
            },
          },
        ]}
        onRowClick={(vendor) => {
          setSelectedVendorId(vendor.id);
        }}
        onSortChange={setSortBy}
        sortBy={sortBy}
      />
      <SimpleDrawer
        drawerTitle={selectedVendor?.name ?? ''}
        isOpen={selectedVendor != null}
        onClose={() => setSelectedVendorId(null)}
        size="xl"
        returnFocusOnClose={false}
        bodyProps={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          paddingBottom: 0,
        }}
      >
        {selectedVendor ? (
          <InvoiceDetailView
            vendorId={selectedVendor.id}
            selectedDates={selectedDates}
            currency={currency}
            initialAdjustment={adjustments.get(selectedVendor.id)}
            onAdjustmentChange={(newAdjustment) => {
              // I'm intentionally mutating here to avoid a re-render of the parent
              adjustments.set(selectedVendor.id, newAdjustment);
            }}
            includeSalesData={includeSalesData}
          />
        ) : null}
      </SimpleDrawer>
    </>
  );
}
